import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, CloudSyncOutlined, DatabaseOutlined, DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined, SettingOutlined, SyncOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Divider, Form, InputNumber, Modal, Row, Select, Space, Spin, Switch, Tabs, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import packageInfo from '../../package.json';
import { clearAllMasterData, getAllSyncStatus } from '../db/database';
import { getSyncConfig, setSyncConfig, syncAll, syncSingleModel, deleteModelData } from '../services/syncService';
import {
    checkForUpdates as checkSwUpdates,
    compareVersions,
    createConnectionListener,
    forceAppUpdate,
    getServiceWorkerVersion
} from '../utils/serviceWorkerUtils';
import { useConfig } from "../contexts/ConfigContext";

const { Title, Paragraph } = Typography;
const { Option } = Select;

const SettingsPage = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [appVersion] = useState(packageInfo.version);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [swVersion, setSwVersion] = useState(null);
    const [debugInfo, setDebugInfo] = useState(null);
    const [checkingVersion, setCheckingVersion] = useState(false);
    const [clearingMasters, setClearingMasters] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [syncStatus, setSyncStatus] = useState([]);
    const [loadingSyncStatus, setLoadingSyncStatus] = useState(false);
    const [syncingModels, setSyncingModels] = useState(new Set());
    const { country } = useConfig();

    useEffect(() => {
        const loadConfig = async () => {
            try {
                setConfigLoaded(false);
                const config = await getSyncConfig();
                if (config) {
                    form.setFieldsValue({
                        syncEnabled: config.enabled,
                        syncInterval: config.interval,
                        syncIntervalUnit: config.intervalUnit,
                        syncOnStartup: config.onStartup,
                        syncWhenOnline: config.whenOnline,
                        syncSourceCounter: config.syncSourceCounter
                    });
                    console.log('Configuración cargada:', config);
                }
            } catch (error) {
                console.error('Error al cargar configuración:', error);
                window.messageApi.error('Error al cargar la configuración');
            } finally {
                setConfigLoaded(true);
            }
        };

        loadConfig();
    }, [form]);

    // Detectar cambios en la conexión y obtener versión del SW
    useEffect(() => {
        // Configurar listener de conexión
        const removeConnectionListener = createConnectionListener(
            () => setIsOnline(true),
            () => setIsOnline(false)
        );

        // Obtener versión del service worker
        const loadSwVersion = async () => {
            const version = await getServiceWorkerVersion();
            setSwVersion(version);

            // Obtener información de debug adicional
            try {
                const cacheNames = await caches.keys();
                const registrations = await navigator.serviceWorker.getRegistrations();
                setDebugInfo({
                    cacheNames: cacheNames,
                    swRegistrations: registrations.length,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString()
                });
            } catch (error) {
                console.warn('Error obteniendo debug info:', error);
            }
        };

        loadSwVersion();

        return removeConnectionListener;
    }, []);

    // Cargar estado de sincronización de modelos
    useEffect(() => {
        if (country) {
            loadSyncStatus();
        }
    }, [country]);

    const loadSyncStatus = async () => {
        setLoadingSyncStatus(true);
        try {
            const status = await getAllSyncStatus(country);
            setSyncStatus(status);
        } catch (error) {
            console.error('Error al cargar estado de sincronización:', error);
            window.messageApi.error('Error al cargar estado de sincronización');
        } finally {
            setLoadingSyncStatus(false);
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            // Transformar los valores del formulario al formato de configuración esperado
            const config = {
                enabled: values.syncEnabled,
                interval: values.syncInterval,
                intervalUnit: values.syncIntervalUnit,
                onStartup: values.syncOnStartup,
                whenOnline: values.syncWhenOnline,
                syncSourceCounter: values.syncSourceCounter
            };

            await setSyncConfig(config);
            window.messageApi.success('Configuración guardada correctamente');
        } catch (error) {
            console.error('Error al guardar configuración:', error);
            window.messageApi.error('Error al guardar la configuración');
        } finally {
            setLoading(false);
        }
    };

    const forceUpdate = async () => {
        setUpdateLoading(true);
        try {
            window.messageApi.loading('Iniciando limpieza completa...', 0);

            await forceAppUpdate();

            window.messageApi.destroy();
            window.messageApi.success('Actualización forzada completada. Recargando desde el servidor...', 2);

            // Esperar un momento antes de recargar con hard refresh
            setTimeout(() => {
                // Forzar recarga completa desde el servidor
                if (window.location.search.includes('force-refresh=true')) {
                    // Si ya tiene el parámetro, usar location.replace para evitar loops
                    window.location.replace(window.location.href);
                } else {
                    // Agregar parámetro para evitar cualquier caché del navegador
                    const separator = window.location.search ? '&' : '?';
                    const newUrl = window.location.href + separator + 'force-refresh=true&t=' + Date.now();
                    window.location.replace(newUrl);
                }
            }, 2000);
        } catch (error) {
            console.error('Error al forzar actualización:', error);
            window.messageApi.destroy();
            window.messageApi.error('Error al forzar la actualización: ' + error.message);
            setUpdateLoading(false);
        }
    };

    const checkForUpdates = async () => {
        try {
            const success = await checkSwUpdates();
            if (success) {
                window.messageApi.info('Verificación de actualizaciones completada');
                // Recargar la versión del SW después de verificar actualizaciones
                const newVersion = await getServiceWorkerVersion();
                setSwVersion(newVersion);
            } else {
                window.messageApi.warning('No se pudo verificar actualizaciones');
            }
        } catch (error) {
            console.error('Error al verificar actualizaciones:', error);
            window.messageApi.error('Error al verificar actualizaciones');
        }
    };

    const refreshVersionInfo = async () => {
        setCheckingVersion(true);
        try {
            // Recargar versión del SW
            const newVersion = await getServiceWorkerVersion();
            setSwVersion(newVersion);

            // Actualizar debug info
            const cacheNames = await caches.keys();
            const registrations = await navigator.serviceWorker.getRegistrations();
            setDebugInfo({
                cacheNames: cacheNames,
                swRegistrations: registrations.length,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
            });

            window.messageApi.success('Información de versión actualizada');
        } catch (error) {
            console.error('Error al actualizar información:', error);
            window.messageApi.error('Error al actualizar información');
        } finally {
            setCheckingVersion(false);
        }
    };

    const handleClearMasterData = () => {
        setShowClearModal(true);
    };

    const confirmClearMasterData = async () => {
        setClearingMasters(true);
        setShowClearModal(false);

        try {
            window.messageApi.loading('Eliminando todos los maestros...', 0);

            const result = await clearAllMasterData();

            window.messageApi.destroy();

            if (result.success) {
                console.log('🔍 Resultado detallado de limpieza:', result);

                const deletedCount = result.totalDeleted || 0;
                const details = Object.entries(result.results)
                    .map(([table, count]) => `${table}: ${count}`)
                    .join(', ');

                console.log('📋 Detalles por tabla:', details);

                if (result.tablesWithErrors && result.tablesWithErrors.length > 0) {
                    window.messageApi.warning(
                        `Maestros eliminados parcialmente. Se eliminaron ${deletedCount} registros, pero hubo errores en algunas tablas.`,
                        7
                    );
                    console.warn('⚠️ Errores encontrados:', result.tablesWithErrors);
                } else {
                    window.messageApi.success(
                        `Maestros eliminados correctamente. Se eliminaron ${deletedCount} registros en total.`,
                        5
                    );
                }

                // OBTENIENDO los datos nuevamente 
                window.messageApi.loading('Obteniendo los datos nuevamente...', 0);
                await syncAll();

                window.messageApi.destroy();

                // Actualizar información de debug
                refreshVersionInfo();
            } else {
                window.messageApi.error(`Error al eliminar maestros: ${result.message}`);
            }
        } catch (error) {
            console.error('Error eliminando maestros:', error);
            window.messageApi.destroy();
            window.messageApi.error('Error inesperado al eliminar los maestros: ' + error.message);
        } finally {
            setClearingMasters(false);
        }
    };

    const handleSyncModel = async (modelKey) => {
        setSyncingModels(prev => new Set([...prev, modelKey]));

        try {
            window.messageApi.loading(`Sincronizando ${modelKey}...`, 0);

            await syncSingleModel(modelKey, country);

            window.messageApi.destroy();
            window.messageApi.success(`${modelKey} sincronizado correctamente`);

            // Recargar estado de sincronización
            await loadSyncStatus();

        } catch (error) {
            console.error(`Error al sincronizar ${modelKey}:`, error);
            window.messageApi.destroy();
            window.messageApi.error(`Error al sincronizar ${modelKey}: ${error.message}`);
        } finally {
            setSyncingModels(prev => {
                const newSet = new Set(prev);
                newSet.delete(modelKey);
                return newSet;
            });
        }
    };

    const handleDeleteModel = async (model) => {
        await deleteModelData(model)
        await loadSyncStatus();
    }

    const handleSyncAll = async () => {
        try {
            window.messageApi.loading('Iniciando sincronización completa...', 0);

            await syncAll();

            window.messageApi.destroy();
            window.messageApi.success('Sincronización completa finalizada');

            // Recargar estado de sincronización
            await loadSyncStatus();

        } catch (error) {
            console.error('Error en sincronización completa:', error);
            window.messageApi.destroy();
            window.messageApi.error('Error en sincronización completa: ' + error.message);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'error':
                return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'never':
                return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
            default:
                return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'success': return '#52c41a';
            case 'error': return '#ff4d4f';
            case 'never': return '#8c8c8c';
            default: return '#8c8c8c';
        }
    };

    const formatDateTime = (date) => {
        if (!date) return 'Nunca';
        return new Date(date).toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (!configLoaded) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card title="Configuración" style={{ maxWidth: 1200, margin: '0 auto' }}>
                <Tabs
                    defaultActiveKey="1"
                    type="card"
                    size="large"
                    items={[
                        {
                            key: '1',
                            label: (
                                <span>
                                    <SettingOutlined />
                                    Configuración General
                                </span>
                            ),
                            children: (
                                <div>
                                    {/* Información de versión y actualización */}
                                    <Title level={4}>Información de la Aplicación</Title>
                                    <Alert
                                        message="Información de la Aplicación"
                                        description={
                                            <div>
                                                <div><strong>Versión de la App:</strong> {appVersion}</div>
                                                {(() => {
                                                    const comparison = compareVersions(appVersion, swVersion);
                                                    const getIcon = () => {
                                                        switch (comparison.status) {
                                                            case 'synced': return '✅';
                                                            case 'mismatch': return '⚠️';
                                                            default: return '❓';
                                                        }
                                                    };
                                                    const getColor = () => {
                                                        switch (comparison.status) {
                                                            case 'synced': return '#52c41a';
                                                            case 'mismatch': return '#ff4d4f';
                                                            default: return '#8c8c8c';
                                                        }
                                                    };

                                                    return (
                                                        <div style={{ color: getColor() }}>
                                                            <strong>Versión en caché:</strong> {swVersion || 'No disponible'}
                                                            <span style={{ marginLeft: '8px' }}>
                                                                {getIcon()} {comparison.message}
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                                <div><strong>Estado de conexión:</strong> {isOnline ? '🟢 Conectado' : '🔴 Sin conexión'}</div>
                                                {/* Información de debug si hay problemas */}
                                                {debugInfo && swVersion !== appVersion && (
                                                    <div style={{
                                                        marginTop: '8px',
                                                        fontSize: '12px',
                                                        color: '#8c8c8c',
                                                        border: '1px solid #d9d9d9',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        backgroundColor: '#fafafa'
                                                    }}>
                                                        <div><strong>Información de Debug:</strong></div>
                                                        <div>Cachés activas: {debugInfo.cacheNames?.length || 0} ({debugInfo.cacheNames?.join(', ') || 'ninguna'})</div>
                                                        <div>Service Workers: {debugInfo.swRegistrations || 0}</div>
                                                        <div>Actualizado: {debugInfo.timestamp ? new Date(debugInfo.timestamp).toLocaleString() : 'N/A'}</div>
                                                    </div>
                                                )}
                                                <div style={{ marginTop: '12px' }}>
                                                    <Space wrap>
                                                        <Button
                                                            type="default"
                                                            icon={<CloudSyncOutlined />}
                                                            onClick={checkForUpdates}
                                                            disabled={!isOnline}
                                                            size="small"
                                                        >
                                                            Verificar Actualizaciones
                                                        </Button>
                                                        <Button
                                                            type="default"
                                                            icon={<ReloadOutlined />}
                                                            onClick={refreshVersionInfo}
                                                            loading={checkingVersion}
                                                            size="small"
                                                        >
                                                            Refrescar Info
                                                        </Button>
                                                        <Button
                                                            type="primary"
                                                            danger
                                                            icon={<ReloadOutlined />}
                                                            onClick={forceUpdate}
                                                            loading={updateLoading}
                                                            size="small"
                                                        >
                                                            Forzar Actualización Completa
                                                        </Button>
                                                    </Space>
                                                </div>

                                                {/* Sección de mantenimiento de datos */}
                                                <Divider style={{ margin: '16px 0 12px 0' }} />
                                                <div>
                                                    <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '500' }}>
                                                        Mantenimiento de Datos
                                                    </div>
                                                    <Button
                                                        type="default"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={handleClearMasterData}
                                                        loading={clearingMasters}
                                                        size="small"
                                                        style={{
                                                            borderColor: '#ff7875',
                                                            color: '#ff4d4f',
                                                            backgroundColor: '#fff2f0'
                                                        }}
                                                    >
                                                        Borrar Todos los Maestros
                                                    </Button>
                                                </div>
                                            </div>
                                        }
                                        type={(() => {
                                            const comparison = compareVersions(appVersion, swVersion);
                                            return comparison.status === 'mismatch' ? "warning" : "info";
                                        })()}
                                        style={{ marginBottom: '24px' }}
                                    />

                                    <Divider />

                                    <Title level={4}>Preferencias de Sincronización</Title>
                                    <Paragraph>
                                        Configure cómo y cuándo la aplicación debe sincronizar los datos con el servidor.
                                    </Paragraph>

                                    <Form
                                        form={form}
                                        layout="vertical"
                                        onFinish={onFinish}
                                        initialValues={{
                                            syncEnabled: true,
                                            syncInterval: 30,
                                            syncIntervalUnit: 'minutos',
                                            syncOnStartup: true,
                                            syncWhenOnline: true,
                                            syncSourceCounter: false
                                        }}
                                    >
                                        <Form.Item
                                            name="syncEnabled"
                                            label="Habilitar sincronización automática"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>

                                        <Form.Item
                                            name="syncInterval"
                                            label="Intervalo de sincronización"
                                            rules={[{ required: true, message: 'Por favor ingrese un intervalo' }]}
                                        >
                                            <InputNumber min={1} max={1440} style={{ width: 120 }} />
                                        </Form.Item>

                                        <Form.Item
                                            name="syncIntervalUnit"
                                            label="Unidad de tiempo"
                                        >
                                            <Select style={{ width: 120 }}>
                                                <Option value="segundos">Segundos</Option>
                                                <Option value="minutos">Minutos</Option>
                                                <Option value="horas">Horas</Option>
                                            </Select>
                                        </Form.Item>

                                        <Form.Item
                                            name="syncOnStartup"
                                            label="Sincronizar al iniciar la aplicación"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>

                                        <Form.Item
                                            name="syncWhenOnline"
                                            label="Sincronizar inmediatamente al recuperar conexión"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>

                                        <Form.Item
                                            name="syncSourceCounter"
                                            label="Sincronizar el contador de importaciones"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>

                                        <Divider />

                                        <Form.Item>
                                            <Button type="primary" htmlType="submit" loading={loading}>
                                                Guardar configuración
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </div>
                            ),
                        },
                        {
                            key: '2',
                            label: (
                                <span>
                                    <DatabaseOutlined />
                                    Estado de Sincronización
                                </span>
                            ),
                            children: (
                                <div>
                                    <Title level={4}>Estado de Sincronización de Modelos</Title>
                                    <Paragraph>
                                        Visualiza el estado actual de sincronización de cada modelo y sincronízalos individualmente según sea necesario.
                                    </Paragraph>

                                    <div style={{ marginBottom: '24px' }}>
                                        <Space style={{ marginBottom: '16px' }}>
                                            <Button
                                                type="primary"
                                                icon={<SyncOutlined />}
                                                onClick={handleSyncAll}
                                                disabled={!isOnline}
                                            >
                                                Sincronizar Todo
                                            </Button>
                                            <Button
                                                icon={<ReloadOutlined />}
                                                onClick={loadSyncStatus}
                                                loading={loadingSyncStatus}
                                            >
                                                Actualizar Estado
                                            </Button>
                                        </Space>

                                        {loadingSyncStatus ? (
                                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                                <Spin size="large" />
                                            </div>
                                        ) : (
                                            <Row gutter={[16, 16]}>
                                                {syncStatus.map((model) => (
                                                    <Col xs={24} sm={12} lg={8} key={model.key}>
                                                        <Card
                                                            size="small"
                                                            style={{
                                                                borderRadius: '8px',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                                borderColor: getStatusColor(model.syncStatus),
                                                                borderWidth: '2px',
                                                                height: '280px',
                                                                display: 'flex',
                                                                flexDirection: 'column'
                                                            }}
                                                            bodyStyle={{
                                                                padding: '16px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                height: '100%'
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                                                                <span style={{ fontSize: '20px', marginRight: '8px' }}>
                                                                    {model.icon}
                                                                </span>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{
                                                                        fontSize: '14px',
                                                                        fontWeight: '600',
                                                                        color: '#262626'
                                                                    }}>
                                                                        {model.name}
                                                                    </div>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        marginTop: '2px'
                                                                    }}>
                                                                        {getStatusIcon(model.syncStatus)}
                                                                        <span style={{
                                                                            marginLeft: '4px',
                                                                            fontSize: '12px',
                                                                            color: getStatusColor(model.syncStatus),
                                                                            fontWeight: '500'
                                                                        }}>
                                                                            {model.syncStatus === 'success' && 'Sincronizado'}
                                                                            {model.syncStatus === 'error' && 'Error'}
                                                                            {model.syncStatus === 'never' && 'Sin sincronizar'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div style={{
                                                                fontSize: '11px',
                                                                color: '#8c8c8c',
                                                                marginBottom: '8px',
                                                                flex: 1,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '4px'
                                                            }}>
                                                                <div>
                                                                    <strong>Último intento:</strong> {formatDateTime(model.lastSyncDate)}
                                                                </div>
                                                                {model.lastSuccessfulSync && (
                                                                    <div>
                                                                        <strong>Último éxito:</strong> {formatDateTime(model.lastSuccessfulSync)}
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <strong>Registros:</strong> {model.totalRecords.toLocaleString()}
                                                                </div>
                                                            </div>

                                                            {model.syncStatus === 'error' && model.errorMessage && (
                                                                <div style={{
                                                                    marginBottom: '8px',
                                                                    padding: '6px',
                                                                    backgroundColor: '#fff2f0',
                                                                    border: '1px solid #ffccc7',
                                                                    borderRadius: '4px',
                                                                    fontSize: '10px',
                                                                    color: '#cf1322',
                                                                    wordBreak: 'break-word',
                                                                    lineHeight: '1.3',
                                                                    maxHeight: '60px',
                                                                    overflow: 'hidden'
                                                                }}>
                                                                    <strong>Error:</strong> {model.errorMessage}
                                                                </div>
                                                            )}

                                                            <Space>
                                                                <Button
                                                                    danger
                                                                    type="primary"
                                                                    size="small"
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={() => handleDeleteModel(model)}
                                                                    disabled={model?.totalRecords == 0}
                                                                    style={{
                                                                        flex: 1,
                                                                        height: '32px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '12px'
                                                                    }}
                                                                >
                                                                    Eliminar
                                                                </Button>

                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    icon={<SyncOutlined />}
                                                                    onClick={() => handleSyncModel(model.key)}
                                                                    loading={syncingModels.has(model.key)}
                                                                    disabled={!isOnline}
                                                                    style={{
                                                                        flex: 1,
                                                                        height: '32px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '12px'
                                                                    }}
                                                                >
                                                                    {syncingModels.has(model.key) ? 'Sincronizando...' : 'Sincronizar'}
                                                                </Button>
                                                            </Space>
                                                        </Card>
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                />
            </Card >

            {/* Modal de confirmación para borrar maestros */}
            < Modal
                title={
                    < div style={{ display: 'flex', alignItems: 'center', color: '#ff4d4f' }}>
                        <ExclamationCircleOutlined style={{ marginRight: '8px', fontSize: '18px' }} />
                        ¿Estás seguro de que quieres borrar todos los maestros ?
                    </div >
                }
                open={showClearModal}
                onOk={confirmClearMasterData}
                onCancel={() => setShowClearModal(false)}
                okText="Sí, borrar todo"
                cancelText="Cancelar"
                okType="danger"
                width={600}
                confirmLoading={clearingMasters}
            >
                <div style={{ padding: '10px 0' }}>
                    <p><strong>Esta acción eliminará TODOS los datos de:</strong></p>
                    <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                        {
                            syncStatus?.map(model =>
                                <li>{model?.name}</li>
                            )
                        }
                    </ul>
                    <Alert
                        message="⚠️ Esta acción NO se puede deshacer"
                        description="Los datos deberán ser sincronizados nuevamente desde el servidor."
                        type="warning"
                        showIcon
                        style={{ marginTop: '16px' }}
                    />
                </div>
            </Modal >
        </div >
    );
};

export default SettingsPage; 